/**
 * FAQ Management Modal
 *
 * Allows restaurant owners to manage custom FAQs for their AI Host.
 * FAQs enable the AI to answer restaurant-specific questions accurately.
 *
 * Features:
 * - List all FAQs with edit/delete buttons
 * - Add new FAQ form (question + answer textareas)
 * - INLINE editing - edit form appears where the FAQ is
 * - Toggle active/inactive status
 * - Character count indicators
 * - Empty state with example FAQs
 *
 * Color scheme: Purple accents for differentiation from other dashboard cards
 *
 * Author: Dine AI
 * Created: December 24, 2025
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Plus,
  Trash2,
  Edit,
  Save,
  HelpCircle,
  Loader,
  Check,
  AlertCircle,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';

// ==================== Types ====================

interface FAQ {
  faq_id: number;
  question: string;
  answer: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface FAQListResponse {
  faqs: FAQ[];
  count: number;
  max_allowed: number;
}

interface FAQManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void; // Callback when FAQs are updated
}

// ==================== Constants ====================

const QUESTION_MIN_LENGTH = 10;
const QUESTION_MAX_LENGTH = 500;
const ANSWER_MIN_LENGTH = 10;
const ANSWER_MAX_LENGTH = 2000;

const EXAMPLE_FAQS = [
  {
    question: "Is Chicken 65 dry or wet?",
    answer: "By default, Chicken 65 is served dry. If served wet with curry, it's called Hyderabad Chicken 65."
  },
  {
    question: "What spice levels do you offer?",
    answer: "We offer customizable spice levels: 1-2 Mild, 3-5 Medium, 6-8 Spicy, 9-10 Extra Spicy."
  },
  {
    question: "Do you have gluten-free options?",
    answer: "Yes, many of our dishes are naturally gluten-free. Please ask your server for specific recommendations."
  }
];

// ==================== Component ====================

const FAQManagementModal: React.FC<FAQManagementModalProps> = ({
  isOpen,
  onClose,
  onUpdate
}) => {
  // State
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [maxAllowed, setMaxAllowed] = useState(50);

  // Add/Edit form state
  const [isAdding, setIsAdding] = useState(false);
  const [editingFaqId, setEditingFaqId] = useState<number | null>(null); // Track by ID for inline editing
  const [formQuestion, setFormQuestion] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Expanded FAQ state (for viewing full text)
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ==================== API Functions ====================

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/virtual-host/faqs');
      const data: FAQListResponse = response.data;
      setFaqs(data.faqs);
      setMaxAllowed(data.max_allowed);
    } catch (error: any) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFAQ = async () => {
    // Validate
    if (formQuestion.length < QUESTION_MIN_LENGTH) {
      toast.error(`Question must be at least ${QUESTION_MIN_LENGTH} characters`);
      return;
    }
    if (formAnswer.length < ANSWER_MIN_LENGTH) {
      toast.error(`Answer must be at least ${ANSWER_MIN_LENGTH} characters`);
      return;
    }

    setSaving(true);
    try {
      if (editingFaqId) {
        // Update existing FAQ
        await apiClient.put(`/virtual-host/faqs/${editingFaqId}`, {
          question: formQuestion,
          answer: formAnswer,
          is_active: formActive
        });
        toast.success('FAQ updated successfully');
      } else {
        // Create new FAQ
        await apiClient.post('/virtual-host/faqs', {
          question: formQuestion,
          answer: formAnswer,
          is_active: formActive
        });
        toast.success('FAQ created successfully');
      }

      // Reset form and refresh
      resetForm();
      fetchFAQs();
      onUpdate?.();
    } catch (error: any) {
      console.error('Error saving FAQ:', error);
      const message = error.response?.data?.detail || 'Failed to save FAQ';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFAQ = async (faqId: number) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    setDeletingId(faqId);
    try {
      await apiClient.delete(`/virtual-host/faqs/${faqId}`);
      toast.success('FAQ deleted');
      fetchFAQs();
      onUpdate?.();
    } catch (error: any) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (faq: FAQ) => {
    try {
      await apiClient.put(`/virtual-host/faqs/${faq.faq_id}`, {
        is_active: !faq.is_active
      });
      toast.success(faq.is_active ? 'FAQ disabled' : 'FAQ enabled');
      fetchFAQs();
      onUpdate?.();
    } catch (error: any) {
      console.error('Error toggling FAQ:', error);
      toast.error('Failed to update FAQ');
    }
  };

  // ==================== Helpers ====================

  const resetForm = () => {
    setIsAdding(false);
    setEditingFaqId(null);
    setFormQuestion('');
    setFormAnswer('');
    setFormActive(true);
  };

  const startEditing = (faq: FAQ) => {
    setEditingFaqId(faq.faq_id);
    setFormQuestion(faq.question);
    setFormAnswer(faq.answer);
    setFormActive(faq.is_active);
    setIsAdding(false);
    setExpandedId(null); // Close any expanded view
  };

  const startAdding = () => {
    resetForm();
    setIsAdding(true);
  };

  const useExample = (example: typeof EXAMPLE_FAQS[0]) => {
    setFormQuestion(example.question);
    setFormAnswer(example.answer);
    setFormActive(true);
    setIsAdding(true);
  };

  // Load FAQs when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchFAQs();
    } else {
      resetForm();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ==================== Inline Edit Form Component ====================
  const renderEditForm = (isNew: boolean = false) => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-purple-50 border border-purple-200 rounded-xl p-4"
    >
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <Edit className="w-4 h-4 text-purple-600" />
        {isNew ? 'Add New FAQ' : 'Editing FAQ'}
      </h3>

      {/* Question */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question
        </label>
        <textarea
          value={formQuestion}
          onChange={(e) => setFormQuestion(e.target.value)}
          placeholder="e.g., Is Chicken 65 dry or wet?"
          maxLength={QUESTION_MAX_LENGTH}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 resize-none text-gray-800"
          rows={2}
        />
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${formQuestion.length < QUESTION_MIN_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
            Min {QUESTION_MIN_LENGTH} characters
          </span>
          <span className={`text-xs ${formQuestion.length > QUESTION_MAX_LENGTH - 50 ? 'text-orange-500' : 'text-gray-500'}`}>
            {formQuestion.length}/{QUESTION_MAX_LENGTH}
          </span>
        </div>
      </div>

      {/* Answer */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Answer
        </label>
        <textarea
          value={formAnswer}
          onChange={(e) => setFormAnswer(e.target.value)}
          placeholder="e.g., By default, Chicken 65 is served dry. If served wet, it's called Hyderabad Chicken 65."
          maxLength={ANSWER_MAX_LENGTH}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 resize-none text-gray-800"
          rows={4}
        />
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${formAnswer.length < ANSWER_MIN_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
            Min {ANSWER_MIN_LENGTH} characters
          </span>
          <span className={`text-xs ${formAnswer.length > ANSWER_MAX_LENGTH - 200 ? 'text-orange-500' : 'text-gray-500'}`}>
            {formAnswer.length}/{ANSWER_MAX_LENGTH}
          </span>
        </div>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setFormActive(!formActive)}
          className={`p-1 rounded transition-colors ${formActive ? 'text-green-600' : 'text-gray-400'}`}
        >
          {formActive ? (
            <ToggleRight className="w-8 h-8" />
          ) : (
            <ToggleLeft className="w-8 h-8" />
          )}
        </button>
        <span className="text-sm text-gray-700">
          {formActive ? 'Active (AI will use this FAQ)' : 'Inactive (AI will not use this FAQ)'}
        </span>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3">
        <button
          onClick={resetForm}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveFAQ}
          disabled={saving || formQuestion.length < QUESTION_MIN_LENGTH || formAnswer.length < ANSWER_MIN_LENGTH}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isNew ? 'Save FAQ' : 'Update FAQ'}
            </>
          )}
        </button>
      </div>
    </motion.div>
  );

  // ==================== Render ====================

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <HelpCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Restaurant FAQs</h2>
              <p className="text-sm text-gray-500">
                {faqs.length} of {maxAllowed} FAQs configured
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-purple-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* Add Button - only show when not adding/editing */}
              {!isAdding && !editingFaqId && faqs.length < maxAllowed && (
                <button
                  onClick={startAdding}
                  className="w-full mb-6 p-4 border-2 border-dashed border-gray-300 hover:border-purple-400 rounded-xl flex items-center justify-center gap-2 text-gray-500 hover:text-purple-600 transition-all group"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Add New FAQ</span>
                </button>
              )}

              {/* Add Form (at top when adding new) */}
              <AnimatePresence>
                {isAdding && (
                  <div className="mb-6">
                    {renderEditForm(true)}
                  </div>
                )}
              </AnimatePresence>

              {/* FAQ List */}
              {faqs.length > 0 ? (
                <div className="space-y-3">
                  {faqs.map((faq) => (
                    <div key={faq.faq_id}>
                      {/* Show edit form inline if this FAQ is being edited */}
                      {editingFaqId === faq.faq_id ? (
                        <AnimatePresence>
                          {renderEditForm(false)}
                        </AnimatePresence>
                      ) : (
                        <motion.div
                          layout
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`border rounded-xl p-4 transition-all ${
                            faq.is_active
                              ? 'bg-white border-gray-200 hover:border-purple-300'
                              : 'bg-gray-50 border-gray-200 opacity-60'
                          }`}
                        >
                          {/* Header Row */}
                          <div className="flex items-start justify-between gap-3">
                            <button
                              onClick={() => setExpandedId(expandedId === faq.faq_id ? null : faq.faq_id)}
                              className="flex-1 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                <span className="font-medium text-gray-900 line-clamp-1">
                                  {faq.question}
                                </span>
                              </div>
                            </button>
                            <div className="flex items-center gap-1">
                              {/* Active Toggle */}
                              <button
                                onClick={() => handleToggleActive(faq)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  faq.is_active
                                    ? 'text-green-600 hover:bg-green-50'
                                    : 'text-gray-400 hover:bg-gray-100'
                                }`}
                                title={faq.is_active ? 'Disable FAQ' : 'Enable FAQ'}
                              >
                                {faq.is_active ? (
                                  <ToggleRight className="w-5 h-5" />
                                ) : (
                                  <ToggleLeft className="w-5 h-5" />
                                )}
                              </button>
                              {/* Edit */}
                              <button
                                onClick={() => startEditing(faq)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit FAQ"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteFAQ(faq.faq_id)}
                                disabled={deletingId === faq.faq_id}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Delete FAQ"
                              >
                                {deletingId === faq.faq_id ? (
                                  <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </button>
                              {/* Expand/Collapse */}
                              <button
                                onClick={() => setExpandedId(expandedId === faq.faq_id ? null : faq.faq_id)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                {expandedId === faq.faq_id ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          <AnimatePresence>
                            {expandedId === faq.faq_id && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-t border-gray-100"
                              >
                                <p className="text-gray-700 text-sm whitespace-pre-wrap">
                                  {faq.answer}
                                </p>
                                {!faq.is_active && (
                                  <div className="mt-2 flex items-center gap-1 text-yellow-600 text-xs">
                                    <AlertCircle className="w-3 h-3" />
                                    <span>This FAQ is disabled and won't be used by AI</span>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              ) : !isAdding && (
                /* Empty State */
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <HelpCircle className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No FAQs Yet</h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    Add common questions about your restaurant, menu items, ingredients, or preparation methods.
                    Your AI Host will use these to answer customer questions accurately.
                  </p>

                  {/* Example FAQs */}
                  <div className="text-left max-w-lg mx-auto">
                    <p className="text-sm font-medium text-gray-700 mb-3">Example FAQs to get started:</p>
                    <div className="space-y-2">
                      {EXAMPLE_FAQS.map((example, idx) => (
                        <button
                          key={idx}
                          onClick={() => useExample(example)}
                          className="w-full p-3 bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-300 rounded-lg text-left transition-all group"
                        >
                          <div className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-gray-400 group-hover:text-purple-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 group-hover:text-purple-700">
                                {example.question}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                {example.answer}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-xs font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded flex-shrink-0">Tip</span>
              <p className="text-xs text-gray-600">
                Best for restaurant-specific knowledge — preparation methods, ingredients, portion sizes, and dietary certifications (e.g., halal, vegan, kosher).
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded flex-shrink-0">Avoid</span>
              <p className="text-xs text-gray-600">
                Allergen safety questions (AI routes these to staff), menu prices (AI reads from menu), or operating hours (AI checks automatically).
              </p>
            </div>
            <div className="flex justify-end mt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FAQManagementModal;
